import * as React from 'react'
import { mergeClasses } from '@fluentui/react-components'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import CalendarRow from './CalendarRow'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { CalendarWeek, DailyTotalDto } from './timeCalendarHelper'

type Props = {
    weeks: CalendarWeek[]
    totals: Map<string, DailyTotalDto>
    resolveNorm: (monday: DateTime) => number | undefined
    colorsActive: boolean
    dayThresholdMinutes: number
    hideWeekend: boolean
    compact: boolean
    onSelectDate: (date: string) => void
}

/**
 * The week table. On the narrowest viewport the weekend is dropped so the five workdays and the total keep a
 * readable width.
 */
export default function CalendarTable(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate } = useTranslation()

    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const visibleDayKeys = props.hideWeekend ? dayKeys.slice(0, 5) : dayKeys

    return (
        <table className={styles.table}>
            <thead>
                <tr>
                    <th className={mergeClasses(styles.headerCell, props.compact ? styles.weekCellCompact : styles.weekCell)}>
                        {translate('time.calendar.column.week')}
                    </th>
                    {visibleDayKeys.map(key => (
                        <th key={key} className={styles.headerCell}>
                            {translate(`time.calendar.column.${key}`)}
                        </th>
                    ))}
                    <th className={mergeClasses(styles.headerCell, styles.totalCell, props.compact && styles.totalCellCompact)}>
                        {translate('time.calendar.column.total')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {props.weeks.map(week => (
                    <CalendarRow
                        key={week.monday.toISODate()}
                        week={week}
                        totals={props.totals}
                        normInMinutes={props.resolveNorm(week.monday)}
                        colorsActive={props.colorsActive}
                        dayThresholdMinutes={props.dayThresholdMinutes}
                        hideWeekend={props.hideWeekend}
                        compact={props.compact}
                        onSelectDate={props.onSelectDate}
                    />
                ))}
            </tbody>
        </table>
    )
}
